<template>
  <!-- Free-trial sign-up (US 9383), in the shared sign-in layout -->
  <auth-layout wide>
    <h1 class="lg-title">{{ $t('register.title') }}</h1>
    <p v-if="!registerLoading" class="lg-sub">
      {{ $t('register.login_link_label') }}
      <a href="#" @click.prevent="goToLogin()" qid="register-go-to-login">{{ $t('register.login_link') }}</a>
    </p>

    <div v-if="registerLoading" class="lg-sent">
      <div class="lg-sent__icon"><span class="lg-spin lg-spin--teal"></span></div>
      <p class="lg-sub">{{ registerLoadingMessage }}</p>
    </div>

    <form v-if="!registerLoading" @submit.prevent="onSubmit" qid="login-form">
      <div v-if="error" class="lg-alert lg-alert--red" qid="register-form-error">
        <font-awesome-icon icon="exclamation-circle"/><div>{{ error }}</div>
      </div>

      <div class="lg-field">
        <label for="rg-company">{{ $t('register.company_name_label') }} <span class="lg-req">*</span></label>
        <div class="lg-input">
          <font-awesome-icon icon="building"/>
          <input id="rg-company" type="text" v-model="form.company_name" autocomplete="organization" required qid="input-register-company">
        </div>
      </div>

      <div class="lg-field">
        <label for="rg-name">{{ $t('register.full_name_label') }} <span class="lg-req">*</span></label>
        <div class="lg-input">
          <font-awesome-icon icon="user"/>
          <input id="rg-name" type="text" v-model="form.name" autocomplete="name" required qid="input-register-name">
        </div>
      </div>

      <div class="lg-field">
        <label for="rg-email">{{ $t('login.work_email_label') }} <span class="lg-req">*</span></label>
        <div class="lg-input">
          <font-awesome-icon icon="envelope"/>
          <input id="rg-email"
                 type="email"
                 v-model.trim="form.email"
                 autocomplete="username"
                 required
                 :placeholder="$t('login.work_email_placeholder')"
                 qid="input-login-email"
          >
        </div>
      </div>

      <div class="lg-field-row">
        <div class="lg-field">
          <label for="rg-password" qid="password">{{ $t('register.password_label') }} <span class="lg-req">*</span></label>
          <div class="lg-input" :class="{ 'lg-input--err': form.password && !validationPass && passwordTouched }">
            <font-awesome-icon icon="lock"/>
            <input id="rg-password"
                   :type="showPassword ? 'text' : 'password'"
                   v-model="form.password"
                   autocomplete="new-password"
                   required
                   qid="input-reset-password"
                   @blur="passwordTouched = true"
            >
            <button type="button"
                    class="lg-input__eye"
                    @click="showPassword = !showPassword"
                    :aria-label="showPassword ? $t('login.hide_password') : $t('login.show_password')"
                    :title="showPassword ? $t('login.hide_password') : $t('login.show_password')"
            >
              <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'"/>
            </button>
          </div>
        </div>
        <div class="lg-field">
          <label for="rg-repeat" qid="confirm-password">{{ $t('register.confirm_password_label') }} <span class="lg-req">*</span></label>
          <div class="lg-input" :class="{ 'lg-input--err': form.password_confirmation && !validationRepeatPass }">
            <font-awesome-icon icon="lock"/>
            <input id="rg-repeat"
                   :type="showPassword ? 'text' : 'password'"
                   v-model="form.password_confirmation"
                   autocomplete="new-password"
                   required
                   qid="input-reset-password_confirmation"
            >
          </div>
        </div>
      </div>
      <div class="lg-hint"
           :class="{ 'lg-hint--err': form.password && !validationPass && passwordTouched, 'lg-hint--ok': validationPass }"
      >
        <font-awesome-icon :icon="validationPass ? 'check' : 'info-circle'"/>
        {{ $t('reset_password.notice.password_requirement') }}
      </div>
      <div v-if="form.password_confirmation && !validationRepeatPass" class="lg-hint lg-hint--err">
        <font-awesome-icon icon="exclamation-circle"/>{{ $t('reset_password.notice.do_not_match') }}
      </div>

      <p class="lg-small">{{ $t('register.first_description_text') }}</p>

      <div class="lg-check-group">
        <label class="lg-check">
          <input type="checkbox" v-model="form.terms" :true-value="1" :false-value="0" name="checkbox-terms">
          <span>
            {{ $t('register.i_agree') }}
            <a href="https://complyon.com/terms-of-use/" target="_blank" rel="noopener">{{ $t('register.terms_of_use') }}</a>
            {{ $t('system.and') }}
            <a href="https://complyon.com/privacy-policy/" target="_blank" rel="noopener">{{ $t('register.privacy_policy') }}</a>
            <span class="lg-req">*</span>
          </span>
        </label>
        <a href="#" class="lg-more" @click.prevent="showPolicyText = !showPolicyText">
          {{ showPolicyText ? $t('system.read_less') : $t('system.read_more') }}
        </a>
        <div v-if="showPolicyText" class="lg-small">
          <p>{{ $t('register.second_description_text') }}</p>
          <p>{{ $t('register.third_description_text') }}</p>
        </div>
        <label class="lg-check">
          <input type="checkbox" v-model="form.newsletter" :true-value="1" :false-value="0" name="checkbox-newsletter">
          <span>{{ $t('register.newsletter') }}</span>
        </label>
      </div>

      <!-- Google recaptcha container -->
      <div id="robot"></div>

      <button type="submit" class="lg-cta" :disabled="buttonDisabled" qid="button-submit-register">
        {{ $t('register.submit_button') }}
      </button>
    </form>
  </auth-layout>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import roleCheck from '@/util/ACL/roleCheck.js'
  import AuthLayout from '@/components/auth/AuthLayout.vue'

  export default {
    components: {
      AuthLayout
    },
    data: function() {
      return {
        form: {
          name: '',
          email: '',
          phone: '',
          company_name: '',
          size: '',
          country: '',
          wrongCredentials: false,
          password: '',
          password_confirmation: '',
          terms: '',
          privacy: '',
          newsletter: '',
          errorMessage: ''
        },
        companySizeItems: [
          { name: '0 - 25', value: '0 - 25' },
          { name: '26 - 200', value: '26 - 200' },
          { name: '201 - 500', value: '201 - 500' },
          { name: '501 - 2,000', value: '501 - 2,000' },
          { name: '2,001 - 10,000', value: '2,001 - 10,000' },
          { name: '10,000+', value: '10,000+' }
        ],
        selectedSize: [],
        countries: [],
        selectedCountry: [],
        forgotPassForm: {
          email: '',
          errorMessage: ''
        },
        showForgotPassword: false,
        error: '',
        buttonDisabled: false,
        registerLoading: false,
        registerLoadingMessage: '',
        showPolicyText: false,
        showPassword: false,
        passwordTouched: false
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getHistoryPage() {
        return this.$store.state.historyPage
      },
      validationPass() {
        let containsCapital = RegExp('[A-Z]');
        let containsSpecial = RegExp('\\W');

        return this.form.password && this.form.password.length > 7 && containsCapital.test(this.form.password) && containsSpecial.test(this.form.password);
      },
      validationRepeatPass() {
        return this.form.password_confirmation && this.form.password === this.form.password_confirmation;
      }
    },
    methods: {

      redirectOnHome(user) {
        if (user.isAdmin) {
          window.location.href = '/administration';
        } else {
          window.location.href = '/';
        }
      },
      setLoadingMessage() {
        let self = this;
        this.registerLoadingMessage = ''

        this.registerLoadingMessage = this.$t('system.register_loading_message_1')

        setTimeout(function(){
          self.registerLoadingMessage = self.$t('system.register_loading_message_2')
        }, 3000);

        setTimeout(function(){
          self.registerLoadingMessage = self.$t('system.register_loading_message_3')
        }, 6000);
      },
      goToLogin() {
        this.$router.push({name: 'login', params: {}})
      },
      loadCountries() {
        let self = this;
        piincHttp.get('countries-with-flag').then(function(response) {
          self.countries = response.data
        }, function() {});
      },
      populatePhone(object) {
        if ((!this.form.phone.length) || (this.form.phone.startsWith('+')) && this.form.phone.length <= 4) {
          this.form.phone = '+' + object.calling_code
        }
      },
      removePhone(object) {
        if ((!this.form.phone.length) || (this.form.phone.startsWith('+')) && this.form.phone.length <= 4) {
          this.form.phone = ''
        }
      },
      onSubmit() {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        let self = this;

        this.$gtag.event('sign_up', {
          'event_category': 'engagement',
          'event_label': 'method'
        })

        if (typeof window.lintrk === 'function') {
          window.lintrk('track', { conversion_id: 6255234 });
        }

        // this.form.size = '';
        // if (this.selectedSize && this.selectedSize.value) {
        //   this.form.size = this.selectedSize.value
        // }

        // this.form.country = '';
        // if (this.selectedCountry && this.selectedCountry.name) {
        //   this.form.country = this.selectedCountry.name
        // }

        // Uncomment this line and comment the one below to enable recaptcha validation
        if (this.form.terms && grecaptcha.getResponse().length) {
          // if (this.form.terms) {
          this.registerLoading = true;
          this.setLoadingMessage()
          piincHttp.post('register', this.form).then(function(response) {
            self.buttonDisabled = false;
            self.$router.push({name: 'login', params: {success: true}})
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.registerLoading = false;
              self.form.wrongCredentials = true;
              self.error = error.data.message;
              if (error.status === 429 || error.status === 422) {
                // TODO: This needs to cover all errors from the array
                self.error = error.data.errors.email[0];
              }
            }
          })
        } else {
          this.buttonDisabled = false;
          this.error = '';
          // if (!this.selectedCountry || (this.selectedCountry && !this.selectedCountry.name)) {
          //   this.error +=  ' ' + this.$t('system.country_error_message')
          // }
          // if (!this.selectedSize || (this.selectedSize && !this.selectedSize.value)) {
          //   this.error +=  ' ' + this.$t('system.company_size_error_message')
          // }
          if (!this.form.terms) {
            this.error +=  ' ' + this.$t('system.terms_error_message')
          }
          if (!grecaptcha.getResponse().length) {
            this.error +=  ' ' + this.$t('system.recaptcha_error_message')
          }
        }
      }
    },
    // Lifecycle hooks
    created() {
      let self = this;

      this.$gtag.pageview('/register')

      // Load recaptcha after load of the page
      setTimeout(function(){
        grecaptcha.render('robot', {
          'sitekey' : '6Ld2RbIbAAAAAAazfFZyE98UYP88B5WbKz4F2eT0'
        });
      }, 500);

      this.loadCountries()
    }
  }
</script>
